import React from 'react'
import { ShoppingCart, RefreshCw, Trash2 } from 'lucide-react'
import ErrorBoundary from './ErrorBoundary'
import useBasketStore from '@/context/BasketStore'

interface BasketErrorBoundaryProps {
    children: React.ReactNode
}

const BasketErrorFallback: React.FC = () => {
    const { clearBasket } = useBasketStore()

    const handleClearBasket = () => {
        clearBasket()
        window.location.reload()
    }

    const handleReload = () => {
        window.location.reload()
    }

    return (
        <div className="min-h-[300px] flex flex-col items-center justify-center p-6 bg-red-50 rounded-lg border border-red-200">
            <ShoppingCart className="h-12 w-12 text-red-500 mb-4" />
            <h2 className="text-lg font-semibold text-red-900 mb-2">
                Basket Error
            </h2>
            <p className="text-red-700 text-center mb-6 max-w-sm text-sm">
                There's an issue with your shopping basket. You can try refreshing or clearing your basket to continue.
            </p>
            
            <div className="flex flex-col gap-3 w-full max-w-xs">
                <button
                    onClick={handleReload}
                    className="flex items-center justify-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                >
                    <RefreshCw className="h-4 w-4" />
                    Refresh Basket
                </button>
                
                <button
                    onClick={handleClearBasket}
                    className="flex items-center justify-center gap-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                >
                    <Trash2 className="h-4 w-4" />
                    Clear Basket & Reload
                </button>
            </div>

            <p className="text-xs text-red-600 mt-4 text-center">
                Note: Clearing your basket will remove all items
            </p>
        </div>
    )
}

const BasketErrorBoundary: React.FC<BasketErrorBoundaryProps> = ({ children }) => {
    return (
        <ErrorBoundary 
            fallback={<BasketErrorFallback />}
            onError={(error, errorInfo) => {
                // Basket-specific error handling
                console.group('🛒 Basket Error Details')
                console.error('Basket component error:', error)
                console.error('Error info:', errorInfo)
                console.groupEnd()

                // You could also log to your analytics or error tracking service
                // trackEvent('basket_error', { error: error.message })
            }}
        >
            {children}
        </ErrorBoundary>
    )
}

export default BasketErrorBoundary